import React, { useCallback, useState } from 'react';
import {
	Col,
	Flex,
	Grid,
	Row,
} from 'antd';
import { Link } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';
import {
	DATE,
	FreightRateType,
	PortActionTypes,
	CargoContractSides,
} from '@shared/utils/constants';
import { formatCurrency } from '@shared/utils/currency';
import { formatQuantity } from '@shared/utils/string';
import { splitActionKey } from '@shared/utils/splitActionKey';
import { formatDate } from '@shared/utils/date';
import normalize from '@shared/utils/normalizeNumber';
import type { GetFixtureDetailsResponse } from '@api/features/fixtures/getFixtureDetails';
import type { SpotFixtureProps } from '@api/models/spot-fixture';
import type { GetCargosAndLaytimesResponse } from '@api/features/laytime/getCargosAndLaytimes';
import { getEstimateDetails } from '@client/lib/api';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import Card from '@client/components/Card/Card';
import Table from '@client/components/Table/Table';
import { Links } from '@client/utils/links';
import Button from '@client/components/Button';
import CargoDetailsModal from '@client/screens/cargos/components/CargoDetailsModal/CargoDetailsModal';
import CargoContractSideTag from '@client/screens/cargos/details/utils/CargoContractSideTag';
import { useVoyage } from '../VoyageProvider/VoyageProvider';
import { useItinerary } from '../ItineraryTab/ItineraryProvider';
import VcPnl from '../VcPnl/VcPnl';
import ItineraryTab from '../ItineraryTab/ItineraryTab';
import SelectItineraryEntryAndDates from '../SelectItineraryEntryAndDates';

type CargoRow = GetCargosAndLaytimesResponse['cargos'][number];

const VcSummaryTab = () => {
	const [creatingCargo, setCreatingCargo] = useState(false);
	const [createCargoModalVisible, setCreateCargoModalVisible] = useState(false);
	const [editingCargoId, setEditingCargoId] = useState<number | null>(null);
	const {
		refreshCargos,
		refreshDetails,
		voyageDetails,
		vcFixtureDetails: fixtureDetails,
		vessel,
		transformedCargos,
	} = useVoyage();

	const {
		refreshItinerary,
	} = useItinerary();

	const { xxl } = Grid.useBreakpoint();

	const [
		estimateDetails,
		_refreshEstimate,
		estimateError,
		loadingEstimate,
	] = useFetchedState(
		async () => {
			const { estimateId } = fixtureDetails as GetFixtureDetailsResponse<SpotFixtureProps>;

			if (estimateId != null) {
				return await getEstimateDetails(estimateId);
			}

			return null;
		},
		[fixtureDetails],
	);

	const sortCargoReletFirst = (a: CargoRow, b: CargoRow) => {
		if (a.contractSide === b.contractSide) {
			return a.id - b.id;
		}

		return a.contractSide === CargoContractSides.VC_OUT ? -1 : 1;
	};

	const getCargoQuantity = useCallback((cargo: CargoRow) => {
		if (
			cargo.contractSide === CargoContractSides.VC_OUT ||
			cargo.contractSide === CargoContractSides.VC_IN
		) {
			return formatQuantity(cargo.quantity, cargo.unit);
		}

		const val = cargo.CargoPorts;
		const quantity = val.reduce((acc, value) => {
			const { action } = splitActionKey(value.portAndActionKey);

			if (action === PortActionTypes.DISCHARGING) {
				return acc;
			}

			const inputQuantity = value.blQuantity ?? value.cpQuantity ?? 0;

			return acc + inputQuantity;
		}, 0);

		if (
			quantity === 0 &&
			!val.some((cp) => normalize(cp.blQuantity) !== 0 ||
				normalize(cp.cpQuantity) !== 0)
		) {
			return formatQuantity(cargo.quantity, cargo.unit);
		}

		return formatQuantity(quantity, cargo.unit);
	}, []);

	if (
		estimateError ||
		loadingEstimate ||
		vessel == null ||
		estimateDetails == null
	) {
		return null;
	}

	const hasRelet = transformedCargos?.some(
		(cargo) => cargo.contractSide === CargoContractSides.VC_IN
	);

	return (
		<Row gutter={[16, 16]}>
			<CargoDetailsModal
				mode={creatingCargo ? 'creatingOnContract' : 'editingOnContract'}
				cargoId={creatingCargo ? null : editingCargoId}
				setEditingCargoId={setEditingCargoId}
				voyageId={voyageDetails.id}
				creatingCargo={creatingCargo}
				onCargoCreation={async () => {
					setCreatingCargo(false);
					await refreshItinerary();
				}}
				visible={createCargoModalVisible}
				onClose={() => {
					setCreateCargoModalVisible(false);
					setCreatingCargo(false);
					setEditingCargoId(null);
				}}
				isTceLocked={false}
				refresh={async () => {
					refreshCargos();
					await refreshDetails();
					await refreshItinerary();
				}}
			/>
			<Col span={18}>
				<Flex vertical gap={10}>
					<SelectItineraryEntryAndDates
						labelWidth={xxl ? 225 : 180}
						columns={4}
					/>
					<Card
						slim
						title="Cargo"
						size="small"
						extra={!hasRelet && (
							<Button
								onClick={() => {
									setCreatingCargo(true);
									setCreateCargoModalVisible(true);
								}}
								size="small"
								icon={<PlusOutlined />}
								type="primary"
							>
								New Cargo
							</Button>
						)}
					>
						<Table
							size="small"
							pagination={false}
							columns={[
								...(hasRelet ? [{
									title: '',
									dataIndex: 'contractSide',
									key: 'contractSide',
									width: 70,
									render: (value: CargoRow['contractSide']) => {
										if (value == null) {
											return null;
										}

										return (
											<CargoContractSideTag contractSide={value} />
										);
									},
								}] : []),
								{
									title: 'Charterer',
									dataIndex: 'chartererName',
									key: 'chartererName',
									render: (charterer) => charterer,
								},
								{
									title: 'Commodity',
									dataIndex: 'type',
									key: 'cargo',
									render: (type, row) => (<Link to={`${Links.Cargo.get(row.id)}`}>{type}</Link>),
									width: 200,
								},
								{
									title: 'Quantity',
									dataIndex: 'quantity',
									key: 'quantity',
									render: (_value, row) => {
										return (
											<Flex>
												{getCargoQuantity(row)}
											</Flex>
										);
									},
								},
								{
									title: 'Load Port(s)',
									dataIndex: 'loadingPortsText',
									key: 'loadingPorts',
								},
								{
									title: 'Discharge Port(s)',
									dataIndex: 'dischargePortsText',
									key: 'dischargePorts',
								},
								{
									title: 'Freight',
									key: 'freight',
									render: (row: CargoRow) => {
										const total = row.freightType === FreightRateType.LUMPSUM ?
											row.freightRate :
											row.quantity * row.freightRate;

										return formatCurrency(total / row.exchangeRate, estimateDetails.currency);
									},
									width: 200,
								},
								{
									title: 'CP Date',
									dataIndex: 'cpDate',
									key: 'cpDate',
									render: (cpDate) => formatDate(cpDate, DATE),
								},
							]}
							dataSource={[...(transformedCargos ?? [])].sort(sortCargoReletFirst)}
						/>
					</Card>
					<ItineraryTab />
				</Flex>
			</Col>
			<Col span={6}>
				<VcPnl
					columnsToHide={['invoiced', 'estimated', 'variance']}
					hideHeaders
					spanOverride={{
						xs: 24,
						sm: 24,
						xl: 24,
						xxl: 24,
					}}
					slim
				/>
			</Col>
		</Row>
	);
};

export default VcSummaryTab;